import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/AdminShell";
import { api } from "@/lib/api";
import { formatAuDate, formatAuDateShort, formatAuDateTime } from "@/lib/date";
import { computeArchiveState, formatDuration } from "@/lib/archive";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { LogOut, Mail, RefreshCcw, Inbox, Flag, CheckCircle2, XCircle, Clock, MailOpen, Download, AlertTriangle, MessageSquare, ArrowLeft, ExternalLink, Send, User, Trash2, Edit3, BookOpen, QrCode } from "lucide-react";
import { UnallocatedView } from "@/pages/admin/UnallocatedView";
import { ChaseList } from "@/pages/admin/ChaseList";
import { BroadcastView } from "@/pages/admin/BroadcastView";
import { VolunteersView } from "@/pages/admin/VolunteersView";

// Auto palette so each day tab / session header has a distinctive colour band.
const DAY_PALETTE = [
  { border: "#002FA7", bg: "#002FA7" },     // deep IKB blue
  { border: "#F59E0B", bg: "#B45309" },     // amber
  { border: "#059669", bg: "#065F46" },     // emerald
  { border: "#7C3AED", bg: "#5B21B6" },     // purple
  { border: "#DC2626", bg: "#991B1B" },     // red
  { border: "#0891B2", bg: "#155E75" },     // cyan
];
const dayColour = (i) => DAY_PALETTE[i % DAY_PALETTE.length];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { eventId } = useParams();
  const [alloc, setAlloc] = useState(null);
  const [emails, setEmails] = useState([]);
  const [chase, setChase] = useState([]);
  const [smsLog, setSmsLog] = useState([]);
  const [bouncesCount, setBouncesCount] = useState(0);
  const [linkMissesCount, setLinkMissesCount] = useState(0);
  const [activeDay, setActiveDay] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "spreadsheet";
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedVolunteerId, setSelectedVolunteerId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [customExportOpen, setCustomExportOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  // Persist the sidebar collapsed state so a browser reload keeps the layout.
  const [unallocCollapsed, setUnallocCollapsed] = useState(() => {
    try { return localStorage.getItem("volmee_unalloc_collapsed") === "1"; } catch { return false; }
  });
  const toggleUnallocCollapsed = useCallback(() => {
    setUnallocCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("volmee_unalloc_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const navigate = useNavigate();

  async function load() {
    try {
      const [a, e, c, s, b, m] = await Promise.all([
        api.get(`/admin/events/${eventId}/allocations`),
        api.get(`/admin/events/${eventId}/emails`),
        api.get(`/admin/events/${eventId}/chase-list`),
        api.get(`/admin/events/${eventId}/sms`),
        api.get(`/admin/events/${eventId}/bounces`).catch(() => ({ data: [] })),
        api.get(`/admin/events/${eventId}/link-diagnostics`).catch(() => ({ data: [] })),
      ]);
      setAlloc(a.data);
      setEmails(e.data);
      setChase(c.data);
      setSmsLog(s.data);
      setBouncesCount((b.data || []).length);
      setLinkMissesCount((m.data || []).length);
      setActiveDay((prev) => prev || (a.data?.days?.length ? a.data.days[0].date : null));
    } catch {
      toast.error("Failed to load event data");
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [eventId]);

  // Load saved export presets — they appear inline in the Export menu so admins
  // can re-download the same set of columns in one click.
  async function loadPresets() {
    try {
      const r = await api.get(`/admin/events/${eventId}/export-presets`);
      setPresets(r.data || []);
    } catch { /* silent — non-critical */ }
  }
  useEffect(() => { loadPresets(); /* eslint-disable-next-line */ }, [eventId]);

  async function handleExport(kind = "allocations") {
    setExporting(true);
    try {
      const url = kind === "volunteers" ? `/admin/events/${eventId}/export/volunteers`
        : kind === "sessions" ? `/admin/events/${eventId}/export/sessions`
        : `/admin/events/${eventId}/export`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const fallback = kind === "volunteers" ? "volunteers.xlsx" : kind === "sessions" ? "sessions.xlsx" : "allocations.xlsx";
      a.href = dlUrl; a.download = m ? m[1] : fallback;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(dlUrl);
      toast.success("Downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  }

  async function handleCustomExport(fields, presetName, layout) {
    setExporting(true);
    try {
      const res = await api.post(
        `/admin/events/${eventId}/export/custom`,
        { fields, preset_name: presetName || null, layout: layout || "volunteers_rows" },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disp = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      a.href = dlUrl; a.download = m ? m[1] : "custom-export.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(dlUrl);
      toast.success(presetName ? `Downloaded — preset "${presetName}" saved.` : "Downloaded");
      if (presetName) loadPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Custom export failed");
    } finally { setExporting(false); }
  }

  async function handleDeletePreset(presetId) {
    if (!window.confirm("Delete this saved preset?")) return;
    try {
      await api.delete(`/admin/events/${eventId}/export-presets/${presetId}`);
      toast.success("Preset deleted");
      loadPresets();
    } catch { toast.error("Delete failed"); }
  }

  const stats = useMemo(() => {
    if (!alloc) return { total: 0, confirmed: 0, pending: 0, declined: 0, vacant: 0 };
    let total = 0, confirmed = 0, pending = 0, declined = 0, vacant = 0;
    for (const day of alloc.days || []) {
      for (const s of day.sessions || []) {
        for (const r of s.roles || []) {
          total += 1;
          const cell = s.cells?.[r];
          if (!cell) vacant += 1;
          else if (cell.status === "declined" || cell.needs_followup) declined += 1;
          else if (cell.status === "confirmed") confirmed += 1;
          else pending += 1;
        }
      }
    }
    return { total, confirmed, pending, declined, vacant };
  }, [alloc]);

  const unread = emails.filter((e) => !e.read).length;

  const VIEW_TITLES = { spreadsheet: "Roster", volunteers: "Registrant profiles", broadcast: "Broadcast", emails: "Email inbox", sms: "SMS log", bounces: "Bounces" };
  return (
    <AdminShell
      event={{ id: eventId, name: alloc?.event?.name, status: alloc?.event?.status, slug: alloc?.event?.slug }}
      active={view}
      onSelectView={(v) => setSearchParams(v === "spreadsheet" ? {} : { view: v })}
      badges={{ emails: unread, sms: smsLog.length, bounces: bouncesCount + linkMissesCount }}
      title={VIEW_TITLES[view]}
      banner={
        <>
      {user?.is_super_admin && alloc?.event && alloc.event.owner_id !== user.id ? (
        <div data-testid="sa-editing-banner" className="bg-[#B00020] text-white">
          <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-6 py-2 text-xs font-black uppercase tracking-widest">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#B00020]">!</span>
            Editing as Super Admin — every action is logged. Event owner: <span className="font-mono normal-case">{alloc?.event?.owner_id || "—"}</span>
            <Link to="/super-admin" className="ml-auto inline-flex items-center gap-1 border border-white/40 px-2 py-0.5 hover:bg-white hover:text-[#B00020]">Back to Super Admin</Link>
          </div>
        </div>
      ) : null}
        </>
      }
    >
      <div className="z-20 border-b border-black/10 bg-white/95 backdrop-blur lg:sticky lg:top-14" data-testid="dashboard-toolbar">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3">
          <h1 className="font-display text-2xl font-black tracking-tighter" data-testid="dashboard-view-title">{VIEW_TITLES[view]}</h1>
          {view === "spreadsheet" ? (
            <div className="flex flex-wrap items-center gap-5 text-xs" data-testid="dashboard-stats">
              <Stat label="Total slots" value={stats.total} testId="stat-total" />
              <Stat label="Confirmed" value={stats.confirmed} color="text-emerald-700" testId="stat-confirmed" />
              <Stat label="Pending" value={stats.pending} color="text-amber-700" testId="stat-pending" />
              <Stat label="Declined / follow-up" value={stats.declined} color="text-red-700" testId="stat-declined" />
              <Stat label="Vacant" value={stats.vacant} color="text-zinc-500" testId="stat-vacant" />
            </div>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2 text-sm">
            <VolunteerFinder eventId={eventId} onOpenVolunteer={setSelectedVolunteerId} />
            <Popover open={exportOpen} onOpenChange={setExportOpen}>
              <PopoverTrigger asChild>
                <button disabled={exporting} data-testid="export-menu-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-1.5 text-xs text-white hover:bg-white hover:text-black disabled:opacity-50">
                  <Download className="h-3.5 w-3.5" /> {exporting ? "Exporting…" : "Export"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end" onClick={(e) => { if (e.target.closest("button") && !e.target.closest("[data-testid^=export-preset-delete-]")) setExportOpen(false); }}>
                <div className="border-b border-black/10 bg-zinc-50 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-500">Export all</div>
                <button onClick={() => handleExport("allocations")} data-testid="export-allocations-btn" className="flex w-full flex-col items-start gap-0.5 border-b border-black/10 px-4 py-3 text-left hover:bg-black hover:text-white">
                  <span className="text-sm font-semibold">Allocation spreadsheet</span>
                  <span className="text-[11px] text-zinc-500 hover:text-white/80">One sheet per day — every session × role</span>
                </button>
                <button onClick={() => handleExport("volunteers")} data-testid="export-volunteers-btn" className="flex w-full flex-col items-start gap-0.5 border-b border-black/10 px-4 py-3 text-left hover:bg-black hover:text-white">
                  <span className="text-sm font-semibold">All volunteers (roster)</span>
                  <span className="text-[11px] text-zinc-500 hover:text-white/80">One row per person, every profile field + itinerary</span>
                </button>
                <button onClick={() => handleExport("sessions")} data-testid="export-sessions-btn" className="flex w-full flex-col items-start gap-0.5 border-b border-black/10 px-4 py-3 text-left hover:bg-black hover:text-white">
                  <span className="text-sm font-semibold">Session team briefing</span>
                  <span className="text-[11px] text-zinc-500 hover:text-white/80">One tab per session — hand to team leaders</span>
                </button>
                <div className="border-b border-t border-black/10 bg-zinc-50 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-500">Custom export</div>
                <button onClick={() => setCustomExportOpen(true)} data-testid="export-custom-btn" className="flex w-full flex-col items-start gap-0.5 border-b border-black/10 px-4 py-3 text-left hover:bg-black hover:text-white">
                  <span className="text-sm font-semibold">Custom export…</span>
                  <span className="text-[11px] text-zinc-500 hover:text-white/80">Pick your own columns — optionally save as a preset for this event.</span>
                </button>
                {presets.length > 0 ? (
                  <div className="max-h-[220px] overflow-y-auto" data-testid="export-presets-list">
                    {presets.map((p) => (
                      <div key={p.id} className="flex items-center gap-1 border-b border-black/10 px-4 py-2 hover:bg-zinc-50">
                        <button
                          onClick={() => handleCustomExport(p.fields, null, p.layout || "volunteers_rows")}
                          data-testid={`export-preset-run-${p.id}`}
                          className="flex-1 text-left"
                          title={`Fields: ${p.fields.join(", ")}${p.layout ? ` · Layout: ${p.layout}` : ""}`}
                        >
                          <span className="block text-sm font-semibold">{p.name}</span>
                          <span className="block text-[10px] text-zinc-500">
                            {p.fields.length} column{p.fields.length !== 1 ? "s" : ""}
                            {p.layout ? ` · ${p.layout === "tab_per_day" ? "1 tab per day" : p.layout === "roles_rows" ? "all-roles flat" : "volunteers list"}` : ""}
                          </span>
                        </button>
                        <button
                          onClick={() => handleDeletePreset(p.id)}
                          data-testid={`export-preset-delete-${p.id}`}
                          className="text-zinc-400 hover:text-red-600"
                          title="Delete preset"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </PopoverContent>
            </Popover>
            <button onClick={load} title="Refresh" className="inline-flex items-center gap-1.5 border border-black/10 px-3 py-1.5 text-xs hover:border-black" data-testid="refresh-btn"><RefreshCcw className="h-3.5 w-3.5" /> Refresh</button>
          </div>
        </div>
      </div>

      {alloc?.event ? <ArchiveCountdownBanner event={{ ...alloc.event, days: alloc.days }} onChanged={load} /> : null}

      <main className="px-6 py-6">
        {view === "spreadsheet" ? (
          <Spreadsheet alloc={alloc} activeDay={activeDay} setActiveDay={setActiveDay} onFollowupToggled={load} chase={chase} eventId={eventId} onOpenVolunteer={setSelectedVolunteerId} unallocCollapsed={unallocCollapsed} onToggleUnallocCollapsed={toggleUnallocCollapsed} />
        ) : view === "emails" ? (
          <EmailsView emails={emails} selected={selectedEmail} setSelected={setSelectedEmail} refresh={load} eventId={eventId} />
        ) : view === "broadcast" ? (
          <BroadcastView alloc={alloc} eventId={eventId} onSent={load} />
        ) : view === "bounces" ? (
          <BouncesView eventId={eventId} />
        ) : view === "volunteers" ? (
          <VolunteersView eventId={eventId} alloc={alloc} />
        ) : (
          <SmsLogView smsLog={smsLog} refresh={load} />
        )}
      </main>
      {selectedVolunteerId ? (
        <VolunteerDetailModal
          eventId={eventId}
          volunteerId={selectedVolunteerId}
          alloc={alloc}
          onClose={() => setSelectedVolunteerId(null)}
          onSmsSent={load}
          onChanged={load}
        />
      ) : null}
      {customExportOpen ? (
        <CustomExportModal
          eventId={eventId}
          onClose={() => setCustomExportOpen(false)}
          onExport={handleCustomExport}
          exporting={exporting}
        />
      ) : null}
    </AdminShell>
  );
}

function Stat({ label, value, color, testId }) {
  // The stat pill numbers previously used `font-mono` whose default 0 glyph looked
  // like an 8 at small sizes. Use the app's display font with tabular-nums so digits
  // stay column-aligned but each numeral is unambiguous.
  return <div className="flex items-center gap-2" data-testid={testId}><span className="text-zinc-500">{label}</span><span className={`font-display font-black tabular-nums text-[15px] leading-none ${color || "text-black"}`}>{value}</span></div>;
}

function ArchiveCountdownBanner({ event, onChanged }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Re-render every 60s so the countdown updates without a page refresh.
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const state = useMemo(() => computeArchiveState(event), [event, tick]);

  async function archiveNow() {
    if (!window.confirm("Archive this event now? It will hide from the public page immediately.")) return;
    try { await api.post(`/admin/events/${event.id}/archive`); toast.success("Event archived"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  }
  async function unarchiveNow() {
    try { await api.post(`/admin/events/${event.id}/unarchive`); toast.success("Restored to Draft — edit and Publish to make it live again"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  }

  if (state.state === "in_progress" || state.state === "upcoming") return null;

  if (state.state === "archived") {
    return (
      <div className="border-b border-amber-300 bg-amber-50" data-testid="archive-banner-archived">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3 px-6 py-3 text-sm">
          <span className="inline-flex items-center gap-1 border border-amber-500 bg-amber-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-900">Archived</span>
          <span className="text-amber-900">This event is archived and no longer visible to the public.</span>
          <span className="text-xs text-amber-800">You can still <b>Edit</b> the details and click <b>Publish</b> to bring it back live — no need to restore first.</span>
          <button onClick={unarchiveNow} data-testid="banner-unarchive-btn" className="ml-auto inline-flex items-center gap-1 border border-amber-600 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-500 hover:text-white">Restore as draft</button>
        </div>
      </div>
    );
  }

  // grace / overdue — event ended, waiting to auto-archive
  const isOverdue = state.state === "overdue";
  return (
    <div className={`border-b ${isOverdue ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"}`} data-testid="archive-banner-countdown">
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3 px-6 py-3 text-sm">
        <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${isOverdue ? "border-red-500 bg-red-100 text-red-900" : "border-blue-500 bg-blue-100 text-blue-900"}`}>
          Event ended
        </span>
        <span className={isOverdue ? "text-red-900" : "text-blue-900"}>
          {isOverdue ? (
            <>Waiting for auto-archive on next scheduler pass (runs every 15 min).</>
          ) : (
            <>Auto-archives in <b data-testid="archive-countdown-remaining" className="font-display font-black tabular-nums">{formatDuration(state.remainingMs)}</b>{state.archivesAt ? <> · {state.archivesAt.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</> : null}</>
          )}
        </span>
        <span className="text-xs text-zinc-600">Once archived, the public page returns 404 but you can still edit and republish.</span>
        <button onClick={archiveNow} data-testid="banner-archive-now-btn" className="ml-auto inline-flex items-center gap-1 border border-black/30 bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white">Archive now</button>
      </div>
    </div>
  );
}

function Spreadsheet({ alloc, activeDay, setActiveDay, onFollowupToggled, chase, eventId, onOpenVolunteer, unallocCollapsed, onToggleUnallocCollapsed }) {
  if (!alloc) return <div className="text-sm text-zinc-500">Loading spreadsheet…</div>;
  if (!alloc.days?.length) {
    return (
      <div className="border border-dashed border-black/20 bg-white p-10 text-center">
        <h3 className="font-display text-2xl font-black">Set up your event first</h3>
        <p className="mt-2 text-sm text-zinc-600">Add days, sessions and roles in the event editor to build your allocation spreadsheet.</p>
        <Link to={`/admin/events/${eventId}/edit`} className="mt-4 inline-block brand-btn px-4 py-2 text-sm">Open editor</Link>
      </div>
    );
  }
  const CHASE = "__chase__";
  const currentTab = activeDay || alloc.days[0].date;
  const unallocated = alloc.unallocated || [];

  async function handleDropToUnallocated() {
    const raw = window.__volmee_drag;
    if (!raw) return;
    if (!raw.vid) { window.__volmee_drag = null; return; }
    const proceed = window.confirm(
      `Move ${raw.name} back to the UNALLOCATED pool?\n\n` +
      `From: ${raw.day} · ${raw.session} · ${raw.role}\n\n` +
      `They will no longer be assigned to this slot. NO email will be sent — they simply return to the unallocated list.\n\n` +
      `OK to move, Cancel to keep them where they are.`
    );
    if (!proceed) { window.__volmee_drag = null; return; }
    try {
      await api.post(`/admin/events/${eventId}/volunteers/${raw.vid}/reassign`, { to_unallocated: true });
      toast.success(`Moved ${raw.name} → Unallocated`);
      onFollowupToggled?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
    finally { window.__volmee_drag = null; }
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
      {/* Left-hand vertical Unallocated pool (feedback #11).
          Sticks near the top of the viewport while the admin scrolls the
          spreadsheet, so they can drag cards straight into any cell. Always
          rendered so it also acts as a drop-target for placed volunteers.
          Width shrinks to a narrow strip when collapsed. */}
      <aside
        className={`lg:sticky lg:top-[160px] lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto shrink-0 ${unallocCollapsed ? "lg:w-12" : "lg:w-72"}`}
        data-testid="unallocated-aside"
      >
        <UnallocatedView
          variant="sidebar"
          items={unallocated}
          days={alloc.days || []}
          eventId={eventId}
          onAssigned={onFollowupToggled}
          onDropHere={handleDropToUnallocated}
          collapsed={unallocCollapsed}
          onToggleCollapse={onToggleUnallocCollapsed}
        />
      </aside>

      <div className="flex-1 min-w-0">
        <Tabs value={currentTab} onValueChange={setActiveDay}>
          <TabsList className="rounded-none bg-transparent p-0 border-b border-black/10 h-auto flex-wrap">
            {alloc.days.map((d, i) => {
              const c = dayColour(i);
              const isActive = currentTab === d.date;
              return (
                <TabsTrigger
                  key={d.date}
                  value={d.date}
                  data-testid={`tab-${d.date}`}
                  style={{
                    borderBottomColor: isActive ? c.border : "transparent",
                    color: isActive ? c.bg : undefined,
                  }}
                  className="rounded-none border-b-4 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-display text-lg font-black tracking-tighter"
                >
                  {formatAuDateShort(d.date)}
                </TabsTrigger>
              );
            })}
            <TabsTrigger value={CHASE} data-testid="tab-chase-list"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF3B30] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-black px-6 py-3 font-display text-lg font-black tracking-tighter">
              <span className="inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[#FF3B30]" /> Chase List
                {chase?.length ? <span className="ml-1 rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[10px] text-white">{chase.length}</span> : null}
              </span>
            </TabsTrigger>
          </TabsList>
          {alloc.days.map((d, i) => (
            <TabsContent key={d.date} value={d.date} className="mt-4">
              <DayView day={d} dayIdx={i} onFollowupToggled={onFollowupToggled} eventId={eventId} onOpenVolunteer={onOpenVolunteer} />
            </TabsContent>
          ))}
          <TabsContent value={CHASE} className="mt-4">
            <ChaseList rows={chase || []} onFollowupToggled={onFollowupToggled} eventId={eventId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DayView({ day, dayIdx, onFollowupToggled, eventId, onOpenVolunteer }) {
  const c = dayColour(dayIdx || 0);
  async function handleDrop(target) {
    const raw = window.__volmee_drag;
    if (!raw) return;
    if (raw.day === target.day && raw.session === target.session && raw.role === target.role) return;
    const proceed = window.confirm(
      `Are you sure you want to move ${raw.name}?\n\n` +
      `From: ${raw.day} · ${raw.session} · ${raw.role}\n` +
      `To:   ${target.day} · ${target.session} · ${target.role}\n\n` +
      `An email will be sent to ${raw.name} asking them to re-confirm the new role.\n\n` +
      `Click OK to move, or Cancel to keep them where they are.`
    );
    if (!proceed) { window.__volmee_drag = null; return; }
    try {
      await api.post(`/admin/events/${eventId}/volunteers/${raw.vid}/reassign`, target);
      toast.success(`Moved ${raw.name} → ${target.session} · ${target.role}. Re-confirmation email sent.`);
      onFollowupToggled?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
    finally { window.__volmee_drag = null; }
  }
  const sessions = day.sessions || [];
  if (!sessions.length) return <div className="border border-dashed border-black/20 bg-white p-10 text-center text-sm text-zinc-500">No event elements on this day.</div>;
  return (
    <div className="space-y-6">
      <div className="border-l-8 px-4 py-3" style={{ borderColor: c.border, backgroundColor: `${c.border}12` }} data-testid={`day-banner-${day.date}`}>
        <div className="text-[10px] font-medium uppercase tracking-widest" style={{ color: c.bg }}>Day of the event</div>
        <div className="font-display text-2xl font-black tracking-tighter" style={{ color: c.bg }}>{formatAuDate(day.date)}</div>
      </div>
      {sessions.map((s) => (
        <div key={s.name} className="overflow-hidden border border-black/10 bg-white" data-testid={`session-block-${s.name.replace(/\s+/g, "")}`}>
          <div className="flex flex-wrap items-baseline gap-3 border-b border-black/5 px-4 py-2 text-white" style={{ backgroundColor: c.bg }}>
            <span className="font-display text-base font-black tracking-tighter">{s.name}</span>
            {s.start || s.end ? <span className="font-mono text-xs text-white/70">{s.start || "?"} – {s.end || "?"}</span> : null}
            {(s.location || "").trim() ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location.trim())}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`session-location-link-admin-${s.name.replace(/\s+/g, "")}`}
                title="Open in Google Maps"
                className="font-mono text-xs text-white/85 underline decoration-dotted hover:decoration-solid hover:text-white"
              >📍 {s.location.trim()}</a>
            ) : null}
            <span className="ml-auto font-mono text-[10px] text-white/60">{(s.roles || []).length} role{(s.roles || []).length !== 1 ? "s" : ""}</span>
          </div>
          <div className="overflow-x-auto">
            <div className="grid min-w-[900px]" style={{ gridTemplateColumns: `repeat(${Math.max(1, (s.roles || []).length)}, minmax(220px, 1fr))` }}>
              {(s.roles || []).map((r) => (
                <div key={r} className="grid-cell !bg-[#F4F4F5] font-display text-xs font-black uppercase tracking-widest">{r}</div>
              ))}
              {(s.roles || []).map((r) => (
                <Cell key={`c-${r}`} cell={s.cells?.[r]} day={day.date} session={s.name} role={r} onFollowupToggled={onFollowupToggled} onDrop={handleDrop} eventId={eventId} onOpenVolunteer={onOpenVolunteer} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Cell({ cell, day, session, role, onFollowupToggled, onDrop, eventId, onOpenVolunteer }) {
  const testId = `cell-${day}-${session.replace(/\s+/g, "")}-${role.replace(/\s+/g, "")}`;
  const [dragOver, setDragOver] = useState(false);
  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => { e.preventDefault(); setDragOver(false); onDrop?.({ day, session, role }); },
  };
  if (!cell) return <div className={`grid-cell vacant ${dragOver ? "!bg-blue-100 ring-2 ring-[#002FA7]" : ""}`} data-testid={testId} {...dropProps}>Vacant</div>;
  const cls = cell.status === "confirmed" ? "confirmed" : cell.status === "declined" ? "declined" : "pending";
  const Icon = cell.status === "confirmed" ? CheckCircle2 : cell.status === "declined" ? XCircle : Clock;
  const draggableProps = {
    draggable: true,
    onDragStart: () => { window.__volmee_drag = { vid: cell.volunteer_id, name: cell.name, day, session, role }; },
    onDragEnd: () => { window.__volmee_drag = null; },
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`grid-cell ${cls} text-left w-full hover:ring-1 hover:ring-black/40 cursor-grab active:cursor-grabbing ${dragOver ? "ring-2 ring-[#002FA7]" : ""} ${cell.has_time_conflict && !cell.conflict_approved ? "ring-2 ring-[#FF3B30]" : ""} relative`} data-testid={testId} {...draggableProps} {...dropProps}>
          {cell.has_time_conflict && !cell.conflict_approved ? (
            <div className="absolute -top-1 -right-1 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#FF3B30] shadow" title="Double-booked: this volunteer sits in two overlapping cells on this day" data-testid={`time-conflict-flag-${cell.volunteer_id}`}>
              <Flag className="h-3 w-3 text-white" />
            </div>
          ) : null}
          <div className="flex items-start justify-between gap-1">
            <div className="min-w-0">
              <div className="truncate font-semibold text-[13px] text-black">{cell.name}</div>
              <div className="truncate text-[11px] text-zinc-600">{cell.email}</div>
            </div>
            <Icon className={`h-3.5 w-3.5 shrink-0 ${cell.status === "confirmed" ? "text-emerald-600" : cell.status === "declined" ? "text-red-600" : "text-amber-600"}`} />
          </div>
          {cell.needs_followup ? (
            <div className="mt-1 inline-flex items-center gap-1 border border-red-300 bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800" data-testid={`cell-followup-badge-${cell.volunteer_id}`}><Flag className="h-2.5 w-2.5" /> FOLLOW UP</div>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        {cell.has_time_conflict && !cell.conflict_approved ? (
          <div className="mb-2 space-y-1.5">
            <div className="inline-flex items-center gap-1.5 border border-[#FF3B30] bg-[#FF3B30]/10 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[#FF3B30]" data-testid="time-conflict-warning">
              <Flag className="h-3 w-3" /> Time conflict — this volunteer is double-booked
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Approve ${cell.name} being in two overlapping roles?\n\nThis marks the conflict as reviewed by you. The red flag will disappear.`)) return;
                try {
                  await api.post(`/admin/events/${eventId}/volunteers/${cell.volunteer_id}/approve-conflict`);
                  toast.success("Conflict approved — the red flag is now cleared.");
                  onFollowupToggled?.();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Approve failed");
                }
              }}
              data-testid={`approve-conflict-btn-${cell.volunteer_id}`}
              className="w-full border border-black bg-black px-2 py-1 text-[11px] font-semibold text-white hover:bg-white hover:text-black"
            >
              Approve conflict
            </button>
          </div>
        ) : cell.has_time_conflict && cell.conflict_approved ? (
          <div className="mb-2 inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700" data-testid={`conflict-approved-badge-${cell.volunteer_id}`}>
            <CheckCircle2 className="h-3 w-3" /> Conflict approved
          </div>
        ) : null}
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">{session} · {role}</div>
        <div className="mt-2 font-display text-lg font-black tracking-tighter">{cell.name}</div>
        <div className="mt-3 space-y-1 text-sm">
          <div><b>Email:</b> {cell.email}</div>
          <div><b>Mobile:</b> {cell.mobile}</div>
          <div><b>Status:</b> <span className="font-mono">{cell.status}</span></div>
          {cell.confirmed_at ? <div><b>Response at:</b> <span className="font-mono text-xs">{formatAuDateTime(cell.confirmed_at)}</span></div> : null}
          <div><b>Needs follow-up:</b> {cell.needs_followup ? "Yes" : "No"}</div>
        </div>
        <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-900" data-testid="spreadsheet-cell-tip">
          Drag this cell onto another cell to reassign. Click <b>Details</b> below to view this volunteer's full itinerary and copy them to more slots.
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => onOpenVolunteer?.(cell.volunteer_id)} data-testid={`open-volunteer-btn-${cell.volunteer_id}`} className="inline-flex items-center justify-center gap-1 border border-black bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-white hover:text-black"><MessageSquare className="h-3 w-3" /> Details / SMS</button>
          <button
            onClick={async () => {
              try {
                const { data } = await api.post(`/admin/events/${eventId}/volunteers/${cell.volunteer_id}/followup`);
                toast.success(data.needs_followup ? `${cell.name} added to Chase list` : `${cell.name} removed from Chase list`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Toggle failed");
              }
              onFollowupToggled?.();
            }}
            data-testid={`cell-toggle-followup-btn-${cell.volunteer_id}`}
            className="border border-black/20 px-3 py-1.5 text-xs font-medium hover:bg-black hover:text-white"
          >
            {cell.needs_followup ? "Clear follow-up flag" : "Flag for follow-up"}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ProviderBadge({ provider, status }) {
  const s = String(status || "").toLowerCase();
  const failed = s.startsWith("failed") || s === "bounced" || s === "complained";
  const positive = s === "delivered" || s === "sent" || s === "opened" || s === "clicked";
  const warn = s === "delayed";
  const cls = failed
    ? "border-red-300 bg-red-50 text-red-800"
    : positive
    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : warn
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : (!provider || provider === "mock")
    ? "border-zinc-300 bg-zinc-50 text-zinc-700"
    : "border-blue-300 bg-blue-50 text-blue-800";
  const label = failed ? (s === "bounced" || s === "complained" ? s : "failed") : (s || provider || "mock");
  return (
    <span
      className={`inline-flex items-center border px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${cls}`}
      data-testid="provider-badge"
      title={status || ""}
    >
      {label}
    </span>
  );
}


function EmailsView({ emails, selected, setSelected, refresh, eventId }) {
  async function open(e) {
    setSelected(e);
    if (!e.read) { await api.post(`/admin/events/${eventId}/emails/${e.id}/read`); refresh?.(); }
  }
  return (
    <div className="grid gap-0 border border-black/10 bg-white md:grid-cols-[380px_1fr] min-h-[600px]">
      <div className="border-r border-black/10 max-h-[80vh] overflow-y-auto">
        {emails.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">No mock emails yet.</div>
        ) : emails.map((e) => (
          <button key={e.id} onClick={() => open(e)} data-testid={`email-item-${e.id}`} className={`block w-full border-b border-black/5 p-4 text-left hover:bg-[#F4F4F5] ${selected?.id === e.id ? "bg-[#F4F4F5]" : ""}`}>
            <div className="flex items-center gap-2">
              {e.read ? <MailOpen className="h-3.5 w-3.5 text-zinc-400" /> : <Mail className="h-3.5 w-3.5 text-[#002FA7]" />}
              <span className={`text-xs uppercase tracking-widest ${e.type === "allocation" ? "text-[#002FA7]" : "text-amber-700"}`}>{e.type}</span>
              <span className="ml-auto font-mono text-[10px] text-zinc-500">{formatAuDateTime(e.created_at)}</span>
            </div>
            <div className={`mt-1 truncate text-sm ${!e.read ? "font-semibold" : ""}`}>{e.subject}</div>
            <div className="truncate text-xs text-zinc-500">to {e.to}</div>
            <div className="mt-1 flex items-center gap-2 text-[10px] font-mono">
              <ProviderBadge provider={e.provider} status={e.provider_status} />
              {e.provider_id ? <span className="truncate text-zinc-400" title={e.provider_id}>#{e.provider_id.slice(0, 8)}</span> : null}
            </div>
          </button>
        ))}
      </div>
      <div className="p-8">
        {!selected ? <div className="text-sm text-zinc-500">Select an email to view.</div> : (
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">{selected.type}</div>
            <h2 className="mt-2 font-display text-2xl font-black tracking-tighter" data-testid="email-subject">{selected.subject}</h2>
            <div className="mt-1 text-xs text-zinc-500">to <span className="font-mono">{selected.to}</span> · <span className="font-mono">{formatAuDateTime(selected.created_at)}</span></div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="email-provider-info">
              <ProviderBadge provider={selected.provider} status={selected.provider_status} />
              {selected.provider_id ? <span className="font-mono text-zinc-500" data-testid="email-provider-id">Resend ID: {selected.provider_id}</span> : null}
              {selected.provider_status ? <span className="font-mono text-zinc-500" data-testid="email-provider-status">Status: {selected.provider_status}</span> : null}
              {selected.last_event_at ? <span className="font-mono text-zinc-500" data-testid="email-last-event">Last event: {formatAuDateTime(selected.last_event_at)}</span> : null}
            </div>
            {selected.webhook_events?.length ? (
              <div className="mt-4 border border-black/10 bg-[#F4F4F5] p-3" data-testid="webhook-events">
                <div className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Delivery timeline</div>
                <ul className="mt-2 space-y-1 text-xs">
                  {selected.webhook_events.map((ev, i) => (
                    <li key={`${ev.type || "wh"}-${ev.at || i}`} data-testid={`webhook-event-${i}`} className="flex items-center gap-2 font-mono">
                      <ProviderBadge provider="resend" status={String(ev.type || "").replace("email.", "")} />
                      <span className="text-zinc-500">{formatAuDateTime(ev.at)}</span>
                      {ev.reason ? <span className="text-red-700">— {ev.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <pre className="mt-6 whitespace-pre-wrap font-body text-sm leading-relaxed text-zinc-800" data-testid="email-body">{selected.body}</pre>
          </div>
        )}
      </div>
    </div>
  );
}


function SmsLogView({ smsLog, refresh }) {
  const [kind, setKind] = useState("all");
  const filtered = smsLog.filter((s) => kind === "all" || (s.kind || "manual") === kind);
  const counts = smsLog.reduce((acc, s) => { const k = s.kind || "manual"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  return (
    <div className="border border-black/10 bg-white p-6" data-testid="sms-log-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#002FA7]">SMS Log</div>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">Every SMS this event has sent</h2>
        </div>
        <button onClick={refresh} className="inline-flex items-center gap-1 border border-black/10 px-3 py-1.5 text-xs hover:border-black" data-testid="sms-log-refresh"><RefreshCcw className="h-3 w-3" /> Refresh</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-1 border-b border-black/10">
        {[
          { k: "all", label: "All", n: smsLog.length },
          { k: "confirmation", label: "Confirmation", n: counts.confirmation || 0 },
          { k: "followup", label: "Follow-up", n: counts.followup || 0 },
          { k: "manual", label: "Manual", n: counts.manual || 0 },
        ].map((t) => (
          <button key={t.k} onClick={() => setKind(t.k)} data-testid={`sms-log-filter-${t.k}`}
            className={`px-3 py-2 text-xs font-black uppercase tracking-widest border-b-2 -mb-px transition-colors ${kind === t.k ? "border-[#002FA7] text-black" : "border-transparent text-zinc-500 hover:text-black"}`}>
            {t.label} {t.n > 0 ? <span className="ml-1 font-mono text-[10px] font-normal text-zinc-500">({t.n})</span> : null}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="mt-8 text-center text-sm italic text-zinc-500">No SMS in this bucket yet.</p>
      ) : (
        <table className="mt-4 w-full text-left text-sm">
          <thead className="border-b border-black/10 text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Sent</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">Message</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`sms-log-row-${s.id}`} className="border-b border-black/5 align-top">
                <td className="px-3 py-3">
                  <KindBadge kind={s.kind || "manual"} />
                </td>
                <td className="px-3 py-3 font-mono text-[11px] text-zinc-600 whitespace-nowrap">{formatAuDateTime(s.created_at)}</td>
                <td className="px-3 py-3">
                  <div className="font-semibold">{s.to_name || "—"}</div>
                  <div className="font-mono text-[11px] text-zinc-500">{s.to}</div>
                </td>
                <td className="px-3 py-3 whitespace-pre-wrap text-xs text-zinc-700">{s.message}</td>
                <td className="px-3 py-3 text-xs">
                  <span className={`inline-block border px-2 py-0.5 text-[10px] font-mono ${s.status?.startsWith("delivered") ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`}>{s.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function KindBadge({ kind }) {
  const map = {
    confirmation: "border-emerald-300 bg-emerald-50 text-emerald-800",
    followup: "border-amber-300 bg-amber-50 text-amber-800",
    manual: "border-blue-300 bg-blue-50 text-blue-800",
  };
  const cls = map[kind] || map.manual;
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${cls}`}>{kind}</span>;
}

function VolunteerDetailModal({ eventId, volunteerId, alloc, onClose, onSmsSent, onChanged }) {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [copyTargets, setCopyTargets] = useState([]); // array of "day||session||role"
  const [copyBusy, setCopyBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/events/${eventId}/volunteers/${volunteerId}`).then((r) => { if (alive) setData(r.data); }).catch(() => { toast.error("Failed to load volunteer"); onClose(); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [eventId, volunteerId]);

  async function sendSms() {
    if (!message.trim()) return toast.error("Type a message first");
    setSending(true);
    try {
      await api.post(`/admin/events/${eventId}/sms/send`, { volunteer_id: volunteerId, message: message.trim() });
      toast.success("SMS sent");
      setMessage("");
      onSmsSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || "SMS failed"); }
    finally { setSending(false); }
  }

  // Build list of currently-vacant slots this volunteer isn't already in, grouped by day.
  const existingSlots = new Set((data?.itinerary_rows || []).map((r) => {
    const s = r.assigned || r.requested || {};
    return `${s.day}||${s.session}||${s.role}`;
  }));
  const vacantByDay = (alloc?.days || []).map((d) => ({
    date: d.date,
    slots: (d.sessions || []).flatMap((s) => (s.roles || []).map((r) => ({
      day: d.date, session: s.name, role: r,
      start: s.start || "", end: s.end || "",
      vacant: !s.cells || s.cells[r] == null,
    }))).filter((slot) => slot.vacant && !existingSlots.has(`${slot.day}||${slot.session}||${slot.role}`)),
  })).filter((d) => d.slots.length);

  function toggleTarget(key) {
    setCopyTargets((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }
  async function pasteCopies() {
    if (!copyTargets.length) return toast.error("Pick at least one vacant slot to paste into.");
    const slots = copyTargets.map((k) => { const [day, session, role] = k.split("||"); return { day, session, role }; });
    const proceed = window.confirm(`Copy ${data.volunteer.name} into ${slots.length} additional slot${slots.length !== 1 ? "s" : ""}?\n\nA fresh confirmation email will be sent covering these new roles. SMS will also be sent if SMS is enabled for this event.`);
    if (!proceed) return;
    setCopyBusy(true);
    try {
      const { data: r } = await api.post(`/admin/events/${eventId}/volunteers/${volunteerId}/copy-to-slots`, { slots });
      toast.success(`Added ${r.added} slot${r.added !== 1 ? "s" : ""}. ${r.email_sent ? "Confirmation email sent." : ""} ${r.sms_sent ? "SMS sent." : ""}`);
      setCopyTargets([]);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Copy failed"); }
    finally { setCopyBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="volunteer-detail-modal">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto bg-white p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        {!data ? <p className="text-sm text-zinc-500">Loading…</p> : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#002FA7]">Volunteer</div>
                <h2 className="mt-1 font-display text-3xl font-black tracking-tighter" data-testid="volunteer-detail-name">{data.volunteer.name}</h2>
                <div className="mt-1 text-sm text-zinc-600">{data.volunteer.email} · <span className="font-mono">{data.volunteer.mobile}</span></div>
              </div>
              <button onClick={onClose} data-testid="volunteer-detail-close" className="border border-black/15 px-3 py-1.5 text-xs hover:bg-black hover:text-white">Close</button>
            </div>

            {/* SMS composer */}
            <div className="mt-6 border border-black/10 bg-[#F9F9FB] p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#002FA7]">
                <MessageSquare className="h-4 w-4" /> Send SMS to {data.volunteer.name}
              </div>
              <textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 480))} className="mt-2 w-full min-h-[100px] border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" placeholder="Type your message… (max 480 chars)" data-testid="sms-composer-textarea" maxLength={480} />
              <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Will be sent via SMS Broadcast to {data.volunteer.mobile}</span>
                <div className="flex items-center gap-2">
                  <span>{message.length}/480</span>
                  <button onClick={sendSms} disabled={sending || !message.trim()} data-testid="sms-composer-send-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40">
                    <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "SEND SMS"}
                  </button>
                </div>
              </div>
            </div>

            {/* Roles / itinerary */}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-zinc-500">This person is signed up for</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(data.itinerary_rows || []).map((r) => {
                  const slot = r.assigned || r.requested || {};
                  return (
                    <li key={r.id} className="flex items-center gap-2 border border-black/10 bg-white px-3 py-2 text-xs">
                      <span className="font-semibold">{slot.day ? formatAuDate(slot.day) : "—"}</span>
                      <span className="font-mono text-zinc-500">{slot.session} · {slot.role}</span>
                      <span className={`ml-auto border px-2 py-0.5 text-[10px] font-mono uppercase ${r.status === "confirmed" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : r.status === "declined" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{r.status}</span>
                    </li>
                  );
                })}
                {!(data.itinerary_rows || []).length ? <li className="text-xs italic text-zinc-400">No slots on record.</li> : null}
              </ul>
            </div>

            {/* Editable profile block — every typed / dropdown field can be updated here.
                Disclaimer + signature stay read-only because they're signed records. */}
            <VolunteerProfileEditor
              key={data.volunteer.id}
              eventId={eventId}
              volunteer={data.volunteer}
              onSaved={() => open(data.volunteer.id)}
            />

            {/* Copy volunteer to more slots — used when a volunteer emails/phones the organiser and asks to be added to additional roles */}
            <div className="mt-6 border-2 border-[#002FA7] bg-[#002FA7]/[0.04] p-4" data-testid="copy-to-slots-block">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#002FA7]">Copy to more slots</div>
                  <h3 className="mt-1 font-display text-lg font-black tracking-tighter">Add {data.volunteer.name} to additional vacant cells</h3>
                  <p className="mt-1 text-[11px] text-zinc-600">Tick every extra slot this volunteer has asked to take on. Save fires one confirmation email (and SMS if enabled) covering only the new slots.</p>
                </div>
                <button
                  type="button"
                  onClick={pasteCopies}
                  disabled={copyBusy || copyTargets.length === 0}
                  data-testid="paste-copies-btn"
                  className="inline-flex items-center gap-1.5 border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40"
                >
                  {copyBusy ? "Copying…" : `Paste into ${copyTargets.length} slot${copyTargets.length !== 1 ? "s" : ""}`}
                </button>
              </div>
              {vacantByDay.length === 0 ? (
                <p className="mt-3 text-xs italic text-zinc-500" data-testid="copy-to-slots-empty">No vacant slots this volunteer isn't already in — every open cell is somewhere else on the spreadsheet.</p>
              ) : (
                <div className="mt-3 max-h-[280px] overflow-y-auto border border-black/10 bg-white p-2" data-testid="copy-to-slots-picker">
                  {vacantByDay.map((d) => (
                    <div key={d.date} className="mb-3">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{formatAuDate(d.date)}</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {d.slots.map((s) => {
                          const k = `${s.day}||${s.session}||${s.role}`;
                          const active = copyTargets.includes(k);
                          return (
                            <button
                              key={k}
                              type="button"
                              onClick={() => toggleTarget(k)}
                              data-testid={`copy-slot-${d.date}-${s.session.replace(/\s+/g, "")}-${s.role.replace(/\s+/g, "")}`}
                              className={`border px-2 py-1 text-[11px] ${active ? "border-black bg-black text-white" : "border-black/15 bg-white hover:border-[#002FA7]"}`}
                            >
                              {s.session}{(s.start || s.end) ? ` (${s.start || "?"}–${s.end || "?"})` : ""} · {s.role}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const SHIRT_OPTIONS = ["", "XS", "S", "M", "L", "XL", "XXL", "3XL"];
const DIETARY_OPTIONS = ["", "None", "Vegetarian", "Vegan", "Gluten-free", "Halal", "Kosher", "Other"];

function VolunteerProfileEditor({ eventId, volunteer, onSaved }) {
  const [form, setForm] = useState({
    name: volunteer.name || "",
    email: volunteer.email || "",
    mobile: volunteer.mobile || "",
    date_of_birth: volunteer.date_of_birth || "",
    shirt_size: volunteer.shirt_size || "",
    location: volunteer.location || "",
    dietary: volunteer.dietary || "",
    medical_notes: volunteer.medical_notes || "",
    organised_group: volunteer.organised_group || "",
    event_experience: volunteer.event_experience || "",
    ec1_name: volunteer.emergency_contact_1?.name || "",
    ec1_mobile: volunteer.emergency_contact_1?.mobile || "",
    ec2_name: volunteer.emergency_contact_2?.name || "",
    ec2_mobile: volunteer.emergency_contact_2?.mobile || "",
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.patch(`/admin/events/${eventId}/volunteers/${volunteer.id}`, form);
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  const F = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="mt-6 border border-black/10 bg-white p-4" data-testid="volunteer-profile-block">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">Profile — editable</div>
        <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="brand-btn px-3 py-1.5 text-xs disabled:opacity-50">{saving ? "Saving…" : "Save profile"}</button>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Full name</span>
          <input value={form.name} onChange={(e) => F("name", e.target.value)} data-testid="profile-name" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Email</span>
          <input type="email" value={form.email} onChange={(e) => F("email", e.target.value)} data-testid="profile-email" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Mobile</span>
          <input value={form.mobile} onChange={(e) => F("mobile", e.target.value)} data-testid="profile-mobile" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Date of birth</span>
          <input type="date" value={form.date_of_birth || ""} max={todayIso} min="1900-01-01" onChange={(e) => F("date_of_birth", e.target.value)} data-testid="profile-dob" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Location</span>
          <select value={form.location} onChange={(e) => F("location", e.target.value)} data-testid="profile-location" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]">
            {["", "Adelaide", "Regional SA", "Interstate"].map((l) => <option key={l || "none"} value={l}>{l || "— select —"}</option>)}
          </select></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Shirt size</span>
          <select value={form.shirt_size} onChange={(e) => F("shirt_size", e.target.value)} data-testid="profile-shirt" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]">
            {SHIRT_OPTIONS.map((s) => <option key={s || "none"} value={s}>{s || "— select —"}</option>)}
          </select></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Dietary</span>
          <select value={DIETARY_OPTIONS.includes(form.dietary) ? form.dietary : "Other"} onChange={(e) => F("dietary", e.target.value === "Other" ? form.dietary || "Other" : e.target.value)} data-testid="profile-dietary" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]">
            {DIETARY_OPTIONS.map((d) => <option key={d || "none"} value={d}>{d || "— select —"}</option>)}
          </select>
          {!DIETARY_OPTIONS.includes(form.dietary) || form.dietary === "Other" ? (
            <input value={form.dietary} onChange={(e) => F("dietary", e.target.value)} data-testid="profile-dietary-custom" placeholder="Enter dietary requirements" className="mt-1 w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" />
          ) : null}
        </label>
        <label className="text-xs md:col-span-2"><span className="mb-1 block font-medium text-zinc-500">Medical notes</span>
          <textarea value={form.medical_notes} onChange={(e) => F("medical_notes", e.target.value)} data-testid="profile-medical" rows={2} className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Organised group / team</span>
          <input value={form.organised_group} onChange={(e) => F("organised_group", e.target.value)} data-testid="profile-group" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
        <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Event experience</span>
          <input value={form.event_experience} onChange={(e) => F("event_experience", e.target.value)} data-testid="profile-experience" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
        <div className="md:col-span-2 mt-2 grid gap-3 border-t border-black/10 pt-3 md:grid-cols-2">
          <div className="md:col-span-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Emergency contacts</div>
          <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #1 name</span>
            <input value={form.ec1_name} onChange={(e) => F("ec1_name", e.target.value)} data-testid="profile-ec1-name" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
          <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #1 mobile</span>
            <input value={form.ec1_mobile} onChange={(e) => F("ec1_mobile", e.target.value)} data-testid="profile-ec1-mobile" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" /></label>
          <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #2 name (optional)</span>
            <input value={form.ec2_name} onChange={(e) => F("ec2_name", e.target.value)} data-testid="profile-ec2-name" className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" /></label>
          <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">EC #2 mobile (optional)</span>
            <input value={form.ec2_mobile} onChange={(e) => F("ec2_mobile", e.target.value)} data-testid="profile-ec2-mobile" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" /></label>
        </div>
        <div className="md:col-span-2 mt-3 border-t border-dashed border-black/10 pt-3 text-[11px] text-zinc-500">
          <b>Read-only records:</b> Signature <span className="font-mono">{volunteer.signature || "—"}</span> · Disclaimer accepted at <span className="font-mono">{volunteer.disclaimer_ack_at ? formatAuDateTime(volunteer.disclaimer_ack_at) : "—"}</span>
        </div>
      </div>
    </div>
  );
}

function BouncesView({ eventId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [misses, setMisses] = useState([]);
  const [missesLoading, setMissesLoading] = useState(true);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/events/${eventId}/bounces`); setRows(data); } catch {}
    finally { setLoading(false); }
  }, [eventId]);
  const loadMisses = useCallback(async () => {
    setMissesLoading(true);
    try { const { data } = await api.get(`/admin/events/${eventId}/link-diagnostics`); setMisses(data); } catch {}
    finally { setMissesLoading(false); }
  }, [eventId]);
  const loadHealth = useCallback(async () => {
    try { const { data } = await api.get(`/admin/resend-webhook-health`); setWebhookHealth(data); } catch { setWebhookHealth(null); }
  }, []);
  useEffect(() => { load(); loadMisses(); loadHealth(); }, [load, loadMisses, loadHealth]);
  async function resolve(id) {
    if (!window.confirm("Delete this bounce notification? Do this once you've manually corrected the volunteer's contact details.")) return;
    try { await api.delete(`/admin/events/${eventId}/bounces/${id}`); toast.success("Bounce cleared"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  }
  async function resolveMiss(id) {
    try { await api.post(`/admin/events/${eventId}/link-diagnostics/${id}/resolve`); toast.success("Link issue dismissed"); loadMisses(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  }
  return (
    <div className="border border-black/10 bg-white p-6" data-testid="bounces-view">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FF3B30]">Bounces to review</div>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">Bounced emails and SMS</h2>
          <p className="mt-1 text-xs text-zinc-600">Delete a notification once you've manually rectified the volunteer's contact details (bad email, wrong mobile, etc.).</p>
        </div>
        <button onClick={() => { load(); loadMisses(); loadHealth(); }} className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-black"><RefreshCcw className="h-3 w-3" /> Refresh</button>
      </div>
      <WebhookHealthChip health={webhookHealth} />
      {loading ? <p className="mt-4 text-sm text-zinc-500">Loading…</p> : rows.length === 0 ? (
        <p className="mt-4 border border-dashed border-black/15 p-8 text-center text-sm italic text-zinc-500" data-testid="bounces-empty">No unresolved bounces right now.</p>
      ) : (
        <ul className="mt-4 divide-y divide-black/5 border border-black/10">
          {rows.map((b) => (
            <li key={b.id} data-testid={`bounce-row-${b.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-black uppercase ${b.channel === "sms" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-blue-300 bg-blue-50 text-blue-800"}`}>{b.channel}</span>
              <span className="font-mono text-xs">{b.to}</span>
              {b.reason ? <span className="text-xs text-red-700">{b.reason}</span> : null}
              <span className="ml-auto font-mono text-[10px] text-zinc-500">{formatAuDateTime(b.detected_at)}</span>
              <button onClick={() => resolve(b.id)} data-testid={`bounce-resolve-${b.id}`} className="inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-[11px] text-red-700 hover:bg-red-600 hover:text-white"><Trash2 className="h-3 w-3" /> Delete notification</button>
            </li>
          ))}
        </ul>
      )}

      {/* Link diagnostics: dead SMS confirmation links */}
      <div className="mt-10" data-testid="link-diagnostics-block">
        <div className="text-xs uppercase tracking-widest text-amber-700">Confirmation link failures</div>
        <h3 className="mt-1 font-display text-xl font-black tracking-tighter">Dead SMS confirmation clicks</h3>
        <p className="mt-1 text-xs text-zinc-600">Every time a volunteer clicks a confirmation link that no longer resolves, it lands here. If you see a spike, your SMS gateway may be mangling URLs, or you may have reassigned volunteers after the SMS went out. Click <span className="font-semibold">Resend confirmation</span> on the affected volunteer from the Chase list to remedy.</p>
        {missesLoading ? <p className="mt-3 text-sm text-zinc-500">Loading…</p> : misses.length === 0 ? (
          <p className="mt-3 border border-dashed border-black/15 p-6 text-center text-sm italic text-zinc-500" data-testid="link-diagnostics-empty">No dead confirmation clicks. All good.</p>
        ) : (
          <ul className="mt-3 divide-y divide-black/5 border border-amber-200 bg-amber-50/40">
            {misses.map((m) => (
              <li key={m.id} data-testid={`link-miss-row-${m.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="inline-flex items-center gap-1 border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-black uppercase text-amber-800">404</span>
                {m.volunteer_name ? <span className="font-semibold text-zinc-800" data-testid={`link-miss-name-${m.id}`}>{m.volunteer_name}</span> : null}
                {m.volunteer_mobile ? <span className="font-mono text-xs text-zinc-800" data-testid={`link-miss-mobile-${m.id}`}>{m.volunteer_mobile}</span> : <span className="text-[11px] italic text-zinc-500">(mobile unknown)</span>}
                <span className="font-mono text-[11px] text-zinc-500" title={m.raw_token}>ref: {(m.sanitised_token || m.raw_token || "").slice(0, 12)}…</span>
                {m.user_agent ? <span className="hidden truncate max-w-[220px] text-[10px] text-zinc-500 lg:inline" title={m.user_agent}>{m.user_agent}</span> : null}
                <span className="ml-auto font-mono text-[10px] text-zinc-500">{formatAuDateTime(m.detected_at)}</span>
                <button onClick={() => resolveMiss(m.id)} data-testid={`link-miss-resolve-${m.id}`} className="inline-flex items-center gap-1 border border-amber-400 px-2 py-1 text-[11px] text-amber-800 hover:bg-amber-500 hover:text-white"><Trash2 className="h-3 w-3" /> Dismiss</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * CustomExportModal — feedback #8. Admin ticks which columns they want, can
 * optionally save the set as a preset (scoped to this event). The parent
 * component owns the actual download + preset save logic; we just capture the
 * selection and hand it back via `onExport(fields, presetName)`.
 */
function CustomExportModal({ eventId, onClose, onExport, exporting }) {
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [presetName, setPresetName] = useState("");
  const [loading, setLoading] = useState(true);
  // Which layout the resulting Excel file uses — 3 flavours the user asked for:
  //   • tab_per_day   — one worksheet per event day, event elements+roles as rows
  //   • roles_rows    — one flat sheet, every day×session×role as a row
  //   • volunteers_rows — legacy: one row per volunteer×slot (kept as default)
  const [layout, setLayout] = useState("volunteers_rows");
  const [otherPresets, setOtherPresets] = useState([]);
  const [copiedFromLabel, setCopiedFromLabel] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, mine] = await Promise.all([
          api.get(`/admin/events/${eventId}/export-fields`),
          api.get(`/admin/export-presets/mine`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setFields(r.data.fields || []);
        // Sensible default — tick every identity + allocation field so the
        // baseline export is useful even before the admin fiddles.
        const defaults = new Set(
          (r.data.fields || [])
            .filter((f) => ["Identity", "Allocation", "Status"].includes(f.group))
            .map((f) => f.key)
        );
        setSelected(defaults);
        // Filter out presets that belong to THIS event — they already show as
        // one-click shortcuts in the export menu, so no need to offer "copy from self".
        const others = (mine.data || []).filter((p) => p.event_id !== eventId);
        setOtherPresets(others);
      } catch { toast.error("Failed to load export fields"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const grouped = useMemo(() => {
    const g = {};
    fields.forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [fields]);

  function toggle(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setCopiedFromLabel(""); // any manual tweak clears the "copied from" label
  }
  function selectAll() { setSelected(new Set(fields.map((f) => f.key))); setCopiedFromLabel(""); }
  function selectNone() { setSelected(new Set()); setCopiedFromLabel(""); }

  function copyFromPreset(presetId) {
    if (!presetId) { setCopiedFromLabel(""); return; }
    const p = otherPresets.find((x) => x.id === presetId);
    if (!p) return;
    // Only tick fields that still exist in the current event's field catalogue
    const validKeys = new Set(fields.map((f) => f.key));
    const next = new Set((p.fields || []).filter((k) => validKeys.has(k)));
    setSelected(next);
    setPresetName(p.name || "");
    if (p.layout) setLayout(p.layout);
    setCopiedFromLabel(`Copied from "${p.name}" (${p.event_name || "another event"})`);
  }

  async function submit(saveAsPreset) {
    const list = fields.map((f) => f.key).filter((k) => selected.has(k)); // preserve field order
    if (!list.length) { toast.error("Tick at least one column."); return; }
    if (saveAsPreset && !presetName.trim()) { toast.error("Name your preset first."); return; }
    await onExport(list, saveAsPreset ? presetName.trim() : null, layout);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="custom-export-modal">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b border-black/10 px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#002FA7]">Custom export</div>
            <h2 className="font-display text-xl font-black tracking-tighter">Pick your columns</h2>
          </div>
          <button onClick={onClose} data-testid="custom-export-close" className="border border-black/15 px-2 py-1 text-xs hover:border-black"><XCircle className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm italic text-zinc-500">Loading fields…</p>
          ) : (
            <>
              {/* Layout picker — top of the modal so admins choose the file
                  shape BEFORE tweaking columns. Every layout uses the same
                  ticked columns; the difference is what each ROW represents. */}
              <div className="mb-4 border border-black/15 bg-zinc-50 p-3" data-testid="custom-export-layout-block">
                <div className="text-[10px] font-black uppercase tracking-widest text-black">How should the export be shaped?</div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <label className={`flex cursor-pointer flex-col gap-1 border p-2 text-xs ${layout === "tab_per_day" ? "border-black bg-black text-white" : "border-black/15 hover:border-black bg-white"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" name="custom-export-layout" value="tab_per_day" checked={layout === "tab_per_day"} onChange={() => setLayout("tab_per_day")} data-testid="custom-export-layout-tab_per_day" />
                      <span className="font-semibold">1 tab per day</span>
                    </span>
                    <span className={`text-[10px] ${layout === "tab_per_day" ? "text-white/80" : "text-zinc-500"}`}>One worksheet per event day. Every event element + role is a row.</span>
                  </label>
                  <label className={`flex cursor-pointer flex-col gap-1 border p-2 text-xs ${layout === "roles_rows" ? "border-black bg-black text-white" : "border-black/15 hover:border-black bg-white"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" name="custom-export-layout" value="roles_rows" checked={layout === "roles_rows"} onChange={() => setLayout("roles_rows")} data-testid="custom-export-layout-roles_rows" />
                      <span className="font-semibold">Roles across all days</span>
                    </span>
                    <span className={`text-[10px] ${layout === "roles_rows" ? "text-white/80" : "text-zinc-500"}`}>One flat sheet. Row per day × event element × role. Volunteer info in columns.</span>
                  </label>
                  <label className={`flex cursor-pointer flex-col gap-1 border p-2 text-xs ${layout === "volunteers_rows" ? "border-black bg-black text-white" : "border-black/15 hover:border-black bg-white"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" name="custom-export-layout" value="volunteers_rows" checked={layout === "volunteers_rows"} onChange={() => setLayout("volunteers_rows")} data-testid="custom-export-layout-volunteers_rows" />
                      <span className="font-semibold">Volunteers list</span>
                    </span>
                    <span className={`text-[10px] ${layout === "volunteers_rows" ? "text-white/80" : "text-zinc-500"}`}>One row per volunteer (per slot). Volunteer info in the left column.</span>
                  </label>
                </div>
              </div>
              {otherPresets.length > 0 ? (
                <div className="mb-4 border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-[#002FA7]">Copy from another event</label>
                    <select
                      onChange={(e) => copyFromPreset(e.target.value)}
                      data-testid="custom-export-copy-from"
                      defaultValue=""
                      className="flex-1 min-w-[200px] border border-black/20 bg-white px-2 py-1 text-xs"
                    >
                      <option value="">Reuse a preset from another event…</option>
                      {otherPresets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {p.event_name || "unknown event"} ({p.fields.length} cols)
                        </option>
                      ))}
                    </select>
                  </div>
                  {copiedFromLabel ? (
                    <p className="mt-1 text-[10px] italic text-[#002FA7]" data-testid="custom-export-copied-label">{copiedFromLabel}</p>
                  ) : (
                    <p className="mt-1 text-[10px] italic text-zinc-500">Instantly tick the same columns that preset used — you can still tweak before downloading.</p>
                  )}
                </div>
              ) : null}
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <button onClick={selectAll} data-testid="custom-export-select-all" className="border border-black/20 px-2 py-1 text-[11px] hover:border-black">Select all</button>
                <button onClick={selectNone} data-testid="custom-export-select-none" className="border border-black/20 px-2 py-1 text-[11px] hover:border-black">Select none</button>
                <span className="ml-auto text-[11px] text-zinc-500" data-testid="custom-export-count">{selected.size} of {fields.length} column{fields.length !== 1 ? "s" : ""} selected</span>
              </div>
              {Object.entries(grouped).map(([group, list]) => (
                <div key={group} className="mb-3">
                  <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-zinc-500">{group}</div>
                  <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                    {list.map((f) => (
                      <label key={f.key} className="flex cursor-pointer items-center gap-2 border border-black/10 bg-white px-2 py-1.5 text-sm hover:border-[#002FA7]">
                        <input
                          type="checkbox"
                          checked={selected.has(f.key)}
                          onChange={() => toggle(f.key)}
                          data-testid={`custom-export-field-${f.key}`}
                          className="h-3.5 w-3.5"
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-black/10 bg-zinc-50 px-5 py-3">
          <label className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">Save as preset (optional — for this event)</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g. Kitchen brief, Sponsor sheet"
              data-testid="custom-export-preset-name"
              className="min-w-[220px] flex-1 border border-black/20 bg-white px-2 py-1.5 text-sm"
            />
            <button
              onClick={() => submit(true)}
              disabled={exporting || !presetName.trim()}
              data-testid="custom-export-save-run-btn"
              className="border border-black/20 bg-white px-3 py-1.5 text-xs hover:border-black disabled:opacity-40"
            >
              Save preset & download
            </button>
            <button
              onClick={() => submit(false)}
              disabled={exporting}
              data-testid="custom-export-download-btn"
              className="border border-black bg-black px-3 py-1.5 text-xs text-white hover:bg-white hover:text-black disabled:opacity-40"
            >
              {exporting ? "Exporting…" : "Download only"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * VolunteerFinder — header-level search box.
 * Types a query, hits the volunteers roster API, and shows a floating dropdown
 * of up to 8 matches (name / email / mobile). Clicking a match opens the
 * volunteer profile modal — works from every view (spreadsheet, bounces,
 * broadcast, sms, mock inbox) because it's rendered in the shared header.
 */
function VolunteerFinder({ eventId, onOpenVolunteer }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const rootRef = useRef(null);

  // Roster is fetched once per open — it's small enough (rarely > a few hundred
  // rows) that a client-side filter is snappier and simpler than debounced
  // server-side calls.
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    if (!open || roster !== null) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/admin/events/${eventId}/volunteers`);
        if (!cancel) setRoster(Array.isArray(data) ? data : []);
      } catch { if (!cancel) setRoster([]); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open, eventId, roster]);

  // Close dropdown when clicking outside the finder — otherwise the panel would
  // stick around while the admin clicks on a spreadsheet cell.
  useEffect(() => {
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false); setCursor(-1);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const src = roster || [];
    if (!q.trim()) { setRows([]); setCursor(-1); return; }
    const needle = q.toLowerCase();
    const matches = [];
    // De-dupe by itinerary_id so one person shows once even if they hold
    // multiple slots on this event.
    const seen = new Set();
    for (const v of src) {
      const key = v.itinerary_id || v.id;
      if (seen.has(key)) continue;
      const hay = `${v.name || ""} ${v.email || ""} ${v.mobile || ""}`.toLowerCase();
      if (hay.includes(needle)) {
        matches.push(v);
        seen.add(key);
        if (matches.length >= 8) break;
      }
    }
    setRows(matches);
    setCursor(matches.length ? 0 : -1);
  }, [q, roster]);

  function pick(v) {
    onOpenVolunteer?.(v.id);
    setOpen(false);
    setQ("");
    setCursor(-1);
  }

  function onKey(e) {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter" && rows[cursor]) { e.preventDefault(); pick(rows[cursor]); }
    else if (e.key === "Escape") { setOpen(false); setQ(""); setCursor(-1); }
  }

  return (
    <div ref={rootRef} className="relative" data-testid="volunteer-finder">
      <div className="flex items-center gap-1.5 border border-black/10 bg-white px-2 py-1 hover:border-black">
        <User className="h-3 w-3 text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Find volunteer by name, email, mobile"
          data-testid="volunteer-finder-input"
          className="w-64 border-0 bg-transparent text-xs outline-none placeholder:text-zinc-400"
        />
        {q ? (
          <button
            type="button"
            onClick={() => { setQ(""); setRows([]); setCursor(-1); }}
            data-testid="volunteer-finder-clear"
            title="Clear"
            className="text-zinc-400 hover:text-black"
          >×</button>
        ) : null}
      </div>
      {open && q.trim() ? (
        <div className="absolute right-0 top-full z-30 mt-1 w-[380px] max-h-[420px] overflow-y-auto border border-black/15 bg-white shadow-xl" data-testid="volunteer-finder-results">
          {loading ? (
            <div className="px-3 py-2 text-xs italic text-zinc-500">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-xs italic text-zinc-500" data-testid="volunteer-finder-empty">No volunteer matches "{q}".</div>
          ) : (
            rows.map((v, i) => (
              <button
                key={v.id}
                type="button"
                onClick={() => pick(v)}
                onMouseEnter={() => setCursor(i)}
                data-testid={`volunteer-finder-result-${v.id}`}
                className={`flex w-full flex-col items-start gap-0.5 border-b border-black/5 px-3 py-2 text-left text-xs ${cursor === i ? "bg-[#002FA7]/[0.08]" : "hover:bg-zinc-50"}`}
              >
                <span className="font-semibold">{v.name}</span>
                <span className="font-mono text-[11px] text-zinc-600">{v.email}{v.mobile ? ` · ${v.mobile}` : ""}</span>
                {v.status ? <span className="text-[10px] uppercase tracking-widest text-zinc-500">{v.status}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}



function WebhookHealthChip({ health }) {
  if (!health) return null;
  const configured = !!health.webhook_secret_configured;
  const lastAt = health.last_event_at ? new Date(health.last_event_at) : null;
  const minsAgo = lastAt ? Math.round((Date.now() - lastAt.getTime()) / 60000) : null;
  const stale = minsAgo === null || minsAgo > 60 * 48; // >48h => stale
  let tone = "border-emerald-300 bg-emerald-50 text-emerald-800";
  let label = "Resend webhook: healthy";
  if (!configured) { tone = "border-amber-400 bg-amber-50 text-amber-900"; label = "Resend webhook secret not configured"; }
  else if (!lastAt) { tone = "border-amber-400 bg-amber-50 text-amber-900"; label = "Resend webhook: never received"; }
  else if (stale) { tone = "border-amber-400 bg-amber-50 text-amber-900"; label = "Resend webhook: silent for >48h"; }
  return (
    <div data-testid="resend-webhook-health" className={`mt-4 flex flex-wrap items-center gap-3 border px-3 py-2 text-xs ${tone}`}>
      <span className="font-black uppercase tracking-widest">{label}</span>
      {lastAt ? <span className="font-mono">Last event: {lastAt.toISOString().slice(0, 16).replace("T", " ")} UTC ({minsAgo} min ago)</span> : null}
      {health.last_event_type ? <span className="font-mono">{health.last_event_type}</span> : null}
      <span className="ml-auto font-mono text-[10px]">Bounces recorded: {health.total_webhook_bounces_recorded ?? 0}</span>
    </div>
  );
}
